import { isPublicEndpoint, routesWithoutAuthorization } from '@/constants/apiRoutes';
import { RequestHeaders } from '@/constants/requestHeaders';
import { getAccessToken, getRefreshToken, setAccessToken } from '@/utils/cookieHelpers';
import { decodeAccessToken, isAccessTokenExpired } from '@/utils/jwtHelpers';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { RequestStatus, type RequestMethod } from '../types';
import { useAuthStore } from '@/stores/authStore';
import router from '@/router';
import { useRefreshTokenStore } from '@/stores/refreshTokenStore';

export const applyTokenInterceptor = (axios: AxiosInstance): void => {
  axios.interceptors.request.use(async (requestConfig: InternalAxiosRequestConfig) => {
    const skipAuthorization
      = isPublicEndpoint(
        routesWithoutAuthorization,
        requestConfig.url || '',
        requestConfig.method as RequestMethod
      ) || requestConfig.headers[RequestHeaders.SkipAuthorization] === 'true';
    delete requestConfig.headers[RequestHeaders.SkipAuthorization];
    if (skipAuthorization) {
      return requestConfig;
    }
    const token = getAccessToken();
    const tokenExpired = isAccessTokenExpired(decodeAccessToken(token)?.exp || 0);
    if (!tokenExpired) {
      requestConfig.headers[RequestHeaders.Authorization] = `${RequestHeaders.Bearer} ${token}`;
    } else {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        logoutAndAbortRequest(requestConfig);

        return requestConfig;
      }
      const { getRefreshTokenPromise } = useRefreshTokenStore();
      const response = await getRefreshTokenPromise(refreshToken);

      if (response.status === RequestStatus.ERROR) {
        logoutAndAbortRequest(requestConfig);
      } else {
        const newAccessToken = response.data.accessToken;
        setAccessToken(newAccessToken);
        requestConfig.headers[RequestHeaders.Authorization]
          = `${RequestHeaders.Bearer} ${newAccessToken}`;
      }
    }

    return requestConfig;
  });

  function logoutAndAbortRequest(requestConfig: AxiosRequestConfig) {
    const authStore = useAuthStore();
    if (authStore.isAuthenticated) {
      authStore.onLogout();
      router.push({ name: 'Home' });
    }
    const controller = new AbortController();
    requestConfig.signal = controller.signal;
    controller.abort();
  }
};
